import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { exportRecordPDF, exportRecordXLSX } from '@/lib/clientExport';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Calendar, CheckCircle2, Clock, XCircle, Download, Loader2 } from 'lucide-react';
import CanAccess from '@/components/ui/CanAccess';
import DrillBreadcrumb from '@/components/ui/DrillBreadcrumb';
import { useBreadcrumb } from '@/hooks/useBreadcrumb';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import FilterBar from '@/components/ui/FilterBar';
import ALCOABadge from '@/components/ui/ALCOABadge';
import AuditTrail from '@/components/ui/AuditTrail';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import ChartCard from '@/components/ui/ChartCard';
import Modal from '@/components/ui/Modal';
import SideDrawer from '@/components/ui/SideDrawer';
import { ChartSkeleton } from '@/components/ui/LoadingSkeleton';
import ProgressBar from '@/components/ui/ProgressBar';
import { format } from 'date-fns';

interface Audit {
  id: string;
  auditCode: string;
  lab: { name: string; labCode: string };
  type: string;
  status: string;
  auditDate: string | null;
  scheduledDate: string;
  auditorName: string;
  score: number;
  findings: number;
  criticalFindings: number;
  summary: string;
  nextAuditDate: string | null;
  scheduledBy?: { name: string; role: string };
  conductedBy?: { name: string; role: string } | null;
  reportedBy?: { name: string; role: string } | null;
  createdAt?: string;
  updatedAt?: string;
  auditTrail?: { id: string; action: string; user: string; role: string; timestamp: string; note?: string; type: 'create' | 'update' | 'approve' | 'close' | 'reject' | 'review' }[];
}

const MOCK_AUDITS: Audit[] = [
  // 2023
  {
    id: 'mock-audit-2301', auditCode: 'AUD-2023-001',
    lab: { name: 'Mumbai QC Central', labCode: 'LAB-001' }, type: 'internal',
    status: 'completed', auditDate: '2023-02-14', scheduledDate: '2023-02-14',
    auditorName: 'Internal QA Team', score: 84, findings: 7, criticalFindings: 1,
    summary: 'Routine internal audit covering documentation, equipment calibration, and personnel training records. One critical gap found in analyst qualification logs. CAPA initiated.',
    nextAuditDate: '2024-02-14',
  },
  {
    id: 'mock-audit-2302', auditCode: 'AUD-2023-002',
    lab: { name: 'Hyderabad API Lab', labCode: 'LAB-003' }, type: 'regulatory',
    status: 'completed', auditDate: '2023-04-20', scheduledDate: '2023-04-20',
    auditorName: 'CDSCO Inspector Team', score: 68, findings: 14, criticalFindings: 3,
    summary: 'CDSCO scheduled inspection of API manufacturing operations. Three critical observations raised: inadequate cleaning validation, missing temperature excursion records, and uncontrolled SOP distribution. Regulatory response submitted.',
    nextAuditDate: '2023-10-20',
  },
  {
    id: 'mock-audit-2303', auditCode: 'AUD-2023-003',
    lab: { name: 'Pune Formulation Lab', labCode: 'LAB-002' }, type: 'external',
    status: 'completed', auditDate: '2023-06-08', scheduledDate: '2023-06-08',
    auditorName: 'Bureau Veritas Audit Team', score: 89, findings: 5, criticalFindings: 0,
    summary: 'ISO 17025 initial accreditation audit conducted by Bureau Veritas. Lab demonstrated strong analytical method control and instrument management. Minor observations on inter-laboratory comparison documentation.',
    nextAuditDate: '2024-06-08',
  },
  {
    id: 'mock-audit-2304', auditCode: 'AUD-2023-004',
    lab: { name: 'Chennai Sterile Lab', labCode: 'LAB-004' }, type: 'surveillance',
    status: 'completed', auditDate: '2023-08-15', scheduledDate: '2023-08-15',
    auditorName: 'Internal QA Team', score: 92, findings: 3, criticalFindings: 0,
    summary: 'WHO GMP surveillance audit for sterile fill-finish area. Environmental monitoring data reviewed; cleanroom classification maintained. Recommendations for enhanced media fill frequency noted.',
    nextAuditDate: '2024-02-15',
  },
  {
    id: 'mock-audit-2305', auditCode: 'AUD-2023-005',
    lab: { name: 'Ahmedabad Stability Lab', labCode: 'LAB-005' }, type: 'internal',
    status: 'completed', auditDate: '2023-11-03', scheduledDate: '2023-11-03',
    auditorName: 'Dr. Preeti Joshi', score: 87, findings: 6, criticalFindings: 0,
    summary: 'Internal audit of stability storage chambers and data integrity. Chamber calibration records verified. Observations on sample retrieval SOPs and backup power testing frequency.',
    nextAuditDate: '2024-11-03',
  },
  // 2024
  {
    id: 'mock-audit-2401', auditCode: 'AUD-2024-001',
    lab: { name: 'Mumbai QC Central', labCode: 'LAB-001' }, type: 'regulatory',
    status: 'completed', auditDate: '2024-01-22', scheduledDate: '2024-01-22',
    auditorName: 'CDSCO Inspector Team', score: 79, findings: 9, criticalFindings: 2,
    summary: 'CDSCO follow-up inspection after 2023 observations. Two critical findings: OOS investigation records incomplete and reference standard management lacking traceability. Improvement plan accepted by authority.',
    nextAuditDate: '2024-07-22',
  },
  {
    id: 'mock-audit-2402', auditCode: 'AUD-2024-002',
    lab: { name: 'Hyderabad API Lab', labCode: 'LAB-003' }, type: 'external',
    status: 'completed', auditDate: '2024-03-11', scheduledDate: '2024-03-11',
    auditorName: 'TÜV SÜD Audit Team', score: 85, findings: 6, criticalFindings: 0,
    summary: 'TÜV SÜD supplier qualification audit for API synthesis operations. GMP compliance assessed against EU GMP Annex 6. Equipment qualification documentation and change control procedures commended.',
    nextAuditDate: '2025-03-11',
  },
  {
    id: 'mock-audit-2403', auditCode: 'AUD-2024-003',
    lab: { name: 'Pune Formulation Lab', labCode: 'LAB-002' }, type: 'internal',
    status: 'completed', auditDate: '2024-05-28', scheduledDate: '2024-05-28',
    auditorName: 'Ramesh Kulkarni', score: 93, findings: 3, criticalFindings: 0,
    summary: 'Mid-year internal audit focusing on dissolution testing and blend uniformity procedures. All critical SOPs current. New tablet press qualification documents verified. Excellent team compliance observed.',
    nextAuditDate: '2024-11-28',
  },
  {
    id: 'mock-audit-2404', auditCode: 'AUD-2024-004',
    lab: { name: 'Chennai Sterile Lab', labCode: 'LAB-004' }, type: 'regulatory',
    status: 'completed', auditDate: '2024-07-09', scheduledDate: '2024-07-09',
    auditorName: 'WHO GMP Inspection Team', score: 88, findings: 7, criticalFindings: 1,
    summary: 'WHO GMP inspection for sterile products pre-qualification. One critical finding on lyophilizer cycle validation documentation. Major findings on environmental excursion trending. Pre-qual report submitted.',
    nextAuditDate: '2025-01-09',
  },
  {
    id: 'mock-audit-2405', auditCode: 'AUD-2024-005',
    lab: { name: 'Ahmedabad Stability Lab', labCode: 'LAB-005' }, type: 'surveillance',
    status: 'completed', auditDate: '2024-09-17', scheduledDate: '2024-09-17',
    auditorName: 'Internal QA Team', score: 95, findings: 2, criticalFindings: 0,
    summary: 'Quarterly surveillance of ICH Q1A stability storage conditions. All chambers within specification. Accelerated stability programs for 3 new products initiated. Data integrity in LIMS validated.',
    nextAuditDate: '2025-03-17',
  },
  {
    id: 'mock-audit-2406', auditCode: 'AUD-2024-006',
    lab: { name: 'Mumbai QC Central', labCode: 'LAB-001' }, type: 'external',
    status: 'completed', auditDate: '2024-11-05', scheduledDate: '2024-11-05',
    auditorName: 'Bureau Veritas Audit Team', score: 91, findings: 4, criticalFindings: 0,
    summary: 'Bureau Veritas ISO 17025 surveillance audit. Method validation records and proficiency testing participation reviewed. Uncertainty of measurement calculations verified. Re-accreditation recommended.',
    nextAuditDate: '2025-11-05',
  },
  // 2025
  {
    id: 'mock-audit-2501', auditCode: 'AUD-2025-001',
    lab: { name: 'Mumbai QC Central', labCode: 'LAB-001' }, type: 'internal',
    status: 'completed', auditDate: '2025-03-10', scheduledDate: '2025-03-10',
    auditorName: 'Dr. Preeti Joshi', score: 91, findings: 4, criticalFindings: 0,
    summary: 'Annual internal audit completed. Documentation and HPLC calibration records found in order. Minor observations on SOP revision schedule.',
    nextAuditDate: '2026-03-10',
  },
  {
    id: 'mock-audit-2502', auditCode: 'AUD-2025-002',
    lab: { name: 'Hyderabad API Lab', labCode: 'LAB-003' }, type: 'regulatory',
    status: 'completed', auditDate: '2025-02-18', scheduledDate: '2025-02-18',
    auditorName: 'CDSCO Inspector Team', score: 76, findings: 11, criticalFindings: 2,
    summary: 'CDSCO regulatory inspection identified critical GDP gaps and incomplete batch records. CAPA required within 30 days.',
    nextAuditDate: '2025-08-18',
  },
  {
    id: 'mock-audit-2503', auditCode: 'AUD-2025-003',
    lab: { name: 'Pune Formulation Lab', labCode: 'LAB-002' }, type: 'external',
    status: 'completed', auditDate: '2025-04-14', scheduledDate: '2025-04-14',
    auditorName: 'Bureau Veritas Audit Team', score: 90, findings: 5, criticalFindings: 0,
    summary: 'ISO 17025 re-accreditation audit completed successfully. Analytical method portfolio reviewed; all compendial methods verified. Laboratory comparability study results met acceptance criteria.',
    nextAuditDate: '2026-04-14',
  },
  {
    id: 'mock-audit-2504', auditCode: 'AUD-2025-004',
    lab: { name: 'Ahmedabad Stability Lab', labCode: 'LAB-005' }, type: 'internal',
    status: 'completed', auditDate: '2025-06-02', scheduledDate: '2025-06-02',
    auditorName: 'Dr. Anjali Mehta', score: 97, findings: 2, criticalFindings: 0,
    summary: 'Internal audit of ICH stability storage facility. All walk-in chambers calibrated and within tolerance. 12-month trending report reviewed. Excellent compliance; commended for real-time LIMS integration.',
    nextAuditDate: '2025-12-02',
  },
  {
    id: 'mock-audit-2505', auditCode: 'AUD-2025-005',
    lab: { name: 'Chennai Sterile Lab', labCode: 'LAB-004' }, type: 'surveillance',
    status: 'completed', auditDate: '2025-08-20', scheduledDate: '2025-08-20',
    auditorName: 'Internal QA Team', score: 88, findings: 5, criticalFindings: 1,
    summary: 'GMP surveillance covering media fill results, EM data, and personnel gowning compliance. Critical finding on investigational threshold exceedance in Grade B area not escalated within SOP timeframe. CAPA raised.',
    nextAuditDate: '2026-02-20',
  },
  {
    id: 'mock-audit-2506', auditCode: 'AUD-2025-006',
    lab: { name: 'Hyderabad API Lab', labCode: 'LAB-003' }, type: 'external',
    status: 'in_progress', auditDate: null, scheduledDate: '2025-11-10',
    auditorName: 'TÜV SÜD Audit Team', score: 0, findings: 0, criticalFindings: 0,
    summary: 'TÜV SÜD follow-up supplier qualification audit in progress for EU GMP compliance confirmation.',
    nextAuditDate: null,
  },
  // 2026
  {
    id: 'mock-audit-2601', auditCode: 'AUD-2026-001',
    lab: { name: 'Pune Formulation Lab', labCode: 'LAB-002' }, type: 'regulatory',
    status: 'scheduled', auditDate: null, scheduledDate: '2026-02-17',
    auditorName: 'CDSCO Inspector Team', score: 0, findings: 0, criticalFindings: 0,
    summary: 'Scheduled CDSCO periodic inspection of oral solid dosage manufacturing. Scope includes data integrity review and change control assessment.',
    nextAuditDate: null,
  },
  {
    id: 'mock-audit-2602', auditCode: 'AUD-2026-002',
    lab: { name: 'Mumbai QC Central', labCode: 'LAB-001' }, type: 'internal',
    status: 'scheduled', auditDate: null, scheduledDate: '2026-03-10',
    auditorName: 'Dr. Preeti Joshi', score: 0, findings: 0, criticalFindings: 0,
    summary: 'Annual planned internal audit for QC laboratory operations. Focus on HPLC system suitability, OOS procedures, and analyst competency.',
    nextAuditDate: null,
  },
  {
    id: 'mock-audit-2603', auditCode: 'AUD-2026-003',
    lab: { name: 'Chennai Sterile Lab', labCode: 'LAB-004' }, type: 'surveillance',
    status: 'scheduled', auditDate: null, scheduledDate: '2026-04-22',
    auditorName: 'Internal QA Team', score: 0, findings: 0, criticalFindings: 0,
    summary: 'Scheduled WHO GMP surveillance audit for sterile manufacturing area. Includes review of aseptic process simulation outcomes and Grade A/B environmental trend analysis.',
    nextAuditDate: null,
  },
  {
    id: 'mock-audit-2604', auditCode: 'AUD-2026-004',
    lab: { name: 'Ahmedabad Stability Lab', labCode: 'LAB-005' }, type: 'external',
    status: 'scheduled', auditDate: null, scheduledDate: '2026-05-15',
    auditorName: 'Bureau Veritas Audit Team', score: 0, findings: 0, criticalFindings: 0,
    summary: 'Scheduled Bureau Veritas ISO 17025 renewal audit for stability testing laboratory accreditation.',
    nextAuditDate: null,
  },
];

const AUDIT_TYPES = ['internal', 'external', 'partner', 'regulatory', 'surveillance'];

const SCOPE_AREA_OPTIONS = ['Documentation', 'Equipment', 'Personnel', 'Processes', 'Quality Systems', 'Facilities', 'Materials'];
const PRE_AUDIT_DOC_OPTIONS = ['SOPs', 'Quality Manual', 'Previous Audit Report', 'CAPA Log', 'Training Records'];
const DURATION_OPTIONS = [
  { value: '0.5', label: '0.5 days' },
  { value: '1', label: '1 day' },
  { value: '2', label: '2 days' },
  { value: '3', label: '3 days' },
];

const WIZARD_STEPS = ['Audit Details', 'Assign Team', 'Scope & Checklist', 'Review'];

const defaultWizardData = {
  auditType: 'internal',
  labId: '',
  scheduledDate: '',
  duration: '1',
  description: '',
  leadAuditor: '',
  coAuditor: '',
  observer: '',
  scopeAreas: [] as string[],
  preAuditDocs: [] as string[],
  specialFocus: '',
};

export default function Audits() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [labId, setLabId] = useState('');
  const [dateRange, setDateRange] = useState('6m');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);

  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState({ ...defaultWizardData });

  const queryClient = useQueryClient();
  const crumbs = useBreadcrumb();

  const { data, isLoading } = useQuery({
    queryKey: ['audits', { search, type: typeFilter, status: statusFilter, page, labId, dateRange }],
    queryFn: () =>
      api.get('/audits', { params: { search, type: typeFilter, status: statusFilter, page, limit: 20, ...(labId && { labId }), range: dateRange } })
        .then((r) => r.data),
  });

  // Auto-open detail when navigated from a notification (?openId=audN)
  const openId = searchParams.get('openId');
  const { data: openAuditData } = useQuery({
    queryKey: ['audit-detail', openId],
    queryFn: () => api.get(`/audits/${openId}`).then(r => r.data.data as Audit),
    enabled: !!openId,
  });
  useEffect(() => {
    if (openAuditData) setSelectedAudit(openAuditData);
  }, [openAuditData]);

  const { data: trendData, isLoading: trendLoading } = useQuery({
    queryKey: ['audit-score-trend', labId, dateRange],
    queryFn: () => api.get('/audits/score-trend', { params: { ...(labId && { labId }), range: dateRange } }).then((r) => r.data.data),
  });

  const { data: calendarData } = useQuery({
    queryKey: ['audit-calendar', year, labId],
    queryFn: () => api.get('/audits/calendar', { params: { year, ...(labId && { labId }) } }).then((r) => r.data.data),
  });

  const { data: labs } = useQuery({
    queryKey: ['labs-all'],
    queryFn: () => api.get('/labs/all').then((r) => r.data.data),
  });

  const scheduleMutation = useMutation({
    mutationFn: (payload: typeof wizardData) => api.post('/audits', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audits'] });
      setShowCreate(false);
      setWizardStep(1);
      setWizardData({ ...defaultWizardData });
    },
  });

  const handleOpenCreate = () => {
    setWizardStep(1);
    setWizardData({ ...defaultWizardData });
    setShowCreate(true);
  };

  const handleExport = (id: string, fmt: 'pdf' | 'xlsx') => {
    const a = selectedAudit;
    if (!a) return;
    const fields: [string, string | number][] = [
      ['Audit Code',      a.auditCode],
      ['Type',            a.type],
      ['Status',          a.status],
      ['Lab',             a.lab?.name ?? '—'],
      ['Auditor',         a.auditorName ?? '—'],
      ['Audit Date',      a.auditDate ? new Date(a.auditDate).toLocaleDateString('en-IN') : '—'],
      ['Scheduled Date',  new Date(a.scheduledDate).toLocaleDateString('en-IN')],
      ['Score',           a.score],
      ['Findings',        a.findings],
      ['Critical Findings', a.criticalFindings],
      ['Next Audit Date', a.nextAuditDate ? new Date(a.nextAuditDate).toLocaleDateString('en-IN') : '—'],
      ['Summary',         a.summary ?? '—'],
      ['Scheduled By',    a.scheduledBy?.name ?? '—'],
      ['Conducted By',    a.conductedBy?.name ?? '—'],
    ];
    if (fmt === 'pdf') exportRecordPDF(`Audit Report — ${a.auditCode}`, a.lab?.name ?? '', fields);
    else exportRecordXLSX(`audit-${id}`, fields);
  };

  const getScoreColor = (score: number) =>
    score >= 85 ? 'text-risk-low' : score >= 70 ? 'text-risk-medium' : 'text-risk-high';

  const toggleArrayItem = (arr: string[], item: string): string[] =>
    arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];

  const columns: Column<Record<string, unknown>>[] = [
    { key: 'auditCode', label: 'Code', width: 'w-28',
      render: (row) => <span className="font-mono text-xs text-text-muted">{String(row.auditCode)}</span> },
    { key: 'lab', label: 'Lab',
      render: (row) => {
        const lab = row.lab as { name: string; labCode: string } | null;
        return <span className="text-sm text-text">{lab?.name || '—'}</span>;
      }},
    { key: 'type', label: 'Type', render: (row) => <PillBadge value={String(row.type)} /> },
    { key: 'status', label: 'Status', render: (row) => <PillBadge value={String(row.status)} showDot /> },
    { key: 'scheduledDate', label: 'Date',
      render: (row) => {
        const d = String(row.auditDate || row.scheduledDate);
        return <span className="text-xs font-mono">{format(new Date(d), 'd MMM yyyy')}</span>;
      }},
    { key: 'score', label: 'Score', align: 'center',
      render: (row) => {
        const score = Number(row.score);
        return (
          <div className="w-20">
            <p className={`text-sm font-bold font-mono text-center ${getScoreColor(score)}`}>{score}</p>
            <ProgressBar value={score} size="sm" />
          </div>
        );
      }},
    { key: 'criticalFindings', label: 'Critical', align: 'center',
      render: (row) => {
        const n = Number(row.criticalFindings);
        return n > 0
          ? <span className="text-xs font-bold text-risk-high bg-risk-high/10 px-2 py-0.5 rounded-full">{n}</span>
          : <span className="text-xs text-text-dim">—</span>;
      }},
    { key: 'auditorName', label: 'Auditor',
      render: (row) => <span className="text-sm text-text">{String(row.auditorName || '—')}</span> },
  ];

  const TREND_COLORS = ['#b07d1a', '#c9922a', '#a07218', '#F0D080', '#F5C842', '#6EE7B7', '#A7F3D0'];
  const trendLabs = trendData && trendData.length > 0 ? Object.keys(trendData[0]).filter((k) => k !== 'label') : [];

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Wizard step content renderer
  const renderWizardStep = () => {
    switch (wizardStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Audit Type *</label>
                <select className="select" value={wizardData.auditType}
                  onChange={(e) => setWizardData((d) => ({ ...d, auditType: e.target.value }))}>
                  {AUDIT_TYPES.map((t) => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Lab *</label>
                {labs && labs.length > 0 ? (
                  <select className="select" value={wizardData.labId}
                    onChange={(e) => setWizardData((d) => ({ ...d, labId: e.target.value }))}>
                    <option value="">Select lab...</option>
                    {labs.map((l: { id: string; name: string }) => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                ) : (
                  <input className="input" placeholder="Enter lab name..." value={wizardData.labId}
                    onChange={(e) => setWizardData((d) => ({ ...d, labId: e.target.value }))} />
                )}
              </div>
              <div>
                <label className="label">Scheduled Date *</label>
                <input type="date" className="input" value={wizardData.scheduledDate}
                  onChange={(e) => setWizardData((d) => ({ ...d, scheduledDate: e.target.value }))} />
              </div>
              <div>
                <label className="label">Duration</label>
                <select className="select" value={wizardData.duration}
                  onChange={(e) => setWizardData((d) => ({ ...d, duration: e.target.value }))}>
                  {DURATION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="label">Description <span className="text-text-dim font-normal">(optional)</span></label>
              <textarea className="input resize-none" rows={3} value={wizardData.description}
                onChange={(e) => setWizardData((d) => ({ ...d, description: e.target.value }))}
                placeholder="Brief description of audit objectives..." />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div>
              <label className="label">Lead Auditor *</label>
              <input className="input" placeholder="Full name..." value={wizardData.leadAuditor}
                onChange={(e) => setWizardData((d) => ({ ...d, leadAuditor: e.target.value }))} />
            </div>
            <div>
              <label className="label">Co-Auditor <span className="text-text-dim font-normal">(optional)</span></label>
              <input className="input" placeholder="Full name..." value={wizardData.coAuditor}
                onChange={(e) => setWizardData((d) => ({ ...d, coAuditor: e.target.value }))} />
            </div>
            <div>
              <label className="label">Observer <span className="text-text-dim font-normal">(optional)</span></label>
              <input className="input" placeholder="Full name..." value={wizardData.observer}
                onChange={(e) => setWizardData((d) => ({ ...d, observer: e.target.value }))} />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-5">
            <div>
              <label className="label mb-2">Scope Areas</label>
              <div className="grid grid-cols-2 gap-2">
                {SCOPE_AREA_OPTIONS.map((area) => (
                  <label key={area} className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={wizardData.scopeAreas.includes(area)}
                      onChange={() => setWizardData((d) => ({ ...d, scopeAreas: toggleArrayItem(d.scopeAreas, area) }))}
                      className="w-4 h-4 rounded border-border accent-gold cursor-pointer"
                    />
                    <span className="text-sm text-text group-hover:text-text-muted transition-colors">{area}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="label mb-2">Pre-Audit Documents</label>
              <div className="grid grid-cols-2 gap-2">
                {PRE_AUDIT_DOC_OPTIONS.map((doc) => (
                  <label key={doc} className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={wizardData.preAuditDocs.includes(doc)}
                      onChange={() => setWizardData((d) => ({ ...d, preAuditDocs: toggleArrayItem(d.preAuditDocs, doc) }))}
                      className="w-4 h-4 rounded border-border accent-gold cursor-pointer"
                    />
                    <span className="text-sm text-text group-hover:text-text-muted transition-colors">{doc}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="label">Special Focus <span className="text-text-dim font-normal">(optional)</span></label>
              <textarea className="input resize-none" rows={3} value={wizardData.specialFocus}
                onChange={(e) => setWizardData((d) => ({ ...d, specialFocus: e.target.value }))}
                placeholder="Any specific areas or concerns to focus on..." />
            </div>
          </div>
        );

      case 4: {
        const labName = labs?.find((l: { id: string; name: string }) => l.id === wizardData.labId)?.name || wizardData.labId || '—';
        return (
          <div className="space-y-4">
            <p className="text-xs text-text-muted">Please review the details before scheduling.</p>
            <div className="space-y-3">
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Audit Details</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                  <span className="text-text-muted">Type</span>
                  <span className="text-text font-medium capitalize">{wizardData.auditType}</span>
                  <span className="text-text-muted">Lab</span>
                  <span className="text-text font-medium">{labName}</span>
                  <span className="text-text-muted">Date</span>
                  <span className="text-text font-mono">{wizardData.scheduledDate || '—'}</span>
                  <span className="text-text-muted">Duration</span>
                  <span className="text-text font-medium">{DURATION_OPTIONS.find((d) => d.value === wizardData.duration)?.label}</span>
                  {wizardData.description && (
                    <>
                      <span className="text-text-muted">Description</span>
                      <span className="text-text">{wizardData.description}</span>
                    </>
                  )}
                </div>
              </div>

              <div className="p-3 bg-bg rounded-lg">
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Audit Team</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                  <span className="text-text-muted">Lead Auditor</span>
                  <span className="text-text font-medium">{wizardData.leadAuditor || '—'}</span>
                  <span className="text-text-muted">Co-Auditor</span>
                  <span className="text-text">{wizardData.coAuditor || '—'}</span>
                  <span className="text-text-muted">Observer</span>
                  <span className="text-text">{wizardData.observer || '—'}</span>
                </div>
              </div>

              <div className="p-3 bg-bg rounded-lg">
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Scope & Checklist</p>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-text-muted text-xs">Scope Areas: </span>
                    <span className="text-text">
                      {wizardData.scopeAreas.length > 0 ? wizardData.scopeAreas.join(', ') : '—'}
                    </span>
                  </div>
                  <div>
                    <span className="text-text-muted text-xs">Pre-Audit Docs: </span>
                    <span className="text-text">
                      {wizardData.preAuditDocs.length > 0 ? wizardData.preAuditDocs.join(', ') : '—'}
                    </span>
                  </div>
                  {wizardData.specialFocus && (
                    <div>
                      <span className="text-text-muted text-xs">Special Focus: </span>
                      <span className="text-text">{wizardData.specialFocus}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="space-y-5">
      <DrillBreadcrumb crumbs={crumbs} />
      <PageHeader
        title="Audit Management"
        subtitle={`${data?.meta?.total || 0} audits recorded`}
        actions={
          <>
            <ALCOABadge />
            <CanAccess roles={['admin', 'qa_director']}>
              <button onClick={handleOpenCreate} className="btn-primary"><Plus size={13} /> Schedule Audit</button>
            </CanAccess>
          </>
        }
      />

      <FilterBar
        labId={labId} onLabChange={(id) => { setLabId(id); setPage(1); }}
        dateRange={dateRange} onDateRangeChange={(r) => { setDateRange(r); setPage(1); }}
      />

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {trendLoading ? <ChartSkeleton height={220} /> : (
          <ChartCard title="Score Trend by Lab" subtitle="Monthly average audit scores" height={220}>
            <ResponsiveContainer>
              <LineChart data={trendData || []} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#C6D8D8" strokeOpacity={0.5} />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6899A0' }} />
                <YAxis domain={[60, 100]} tick={{ fontSize: 10, fill: '#6899A0' }} width={25} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {trendLabs.map((lab, i) => (
                  <Line key={lab} type="monotone" dataKey={lab} stroke={TREND_COLORS[i % TREND_COLORS.length]}
                    strokeWidth={2} dot={false} name={lab} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* Calendar heatmap */}
        <ChartCard
          title="Audit Calendar"
          subtitle={`Audit activity ${year}`}
          height={220}
          action={
            <div className="flex items-center gap-1">
              <button onClick={() => setYear((y) => y - 1)} className="p-1 hover:bg-bg rounded text-text-muted text-xs">◀</button>
              <span className="text-xs font-mono text-text-muted w-12 text-center">{year}</span>
              <button onClick={() => setYear((y) => y + 1)} className="p-1 hover:bg-bg rounded text-text-muted text-xs">▶</button>
            </div>
          }
        >
          <div className="grid grid-cols-12 gap-1 h-full items-center pt-2">
            {MONTHS.map((month, idx) => {
              const monthData = calendarData?.find((d: { month: number; count: number; avgScore: number }) => d.month === idx + 1);
              const count = monthData?.count || 0;
              const avg = monthData?.avgScore || 0;
              return (
                <div key={month} className="flex flex-col items-center gap-1">
                  <div
                    className="w-full aspect-square rounded flex items-center justify-center text-[9px] font-bold"
                    style={{
                      backgroundColor: count === 0 ? '#E8F4F4' : avg >= 85 ? '#D1FAE5' : avg >= 70 ? '#FEF3C7' : '#FEE2E2',
                      color: count === 0 ? '#9ABCBC' : avg >= 85 ? '#16A34A' : avg >= 70 ? '#D97706' : '#DC2626',
                    }}
                  >
                    {count > 0 ? count : ''}
                  </div>
                  <span className="text-[9px] text-text-dim">{month}</span>
                </div>
              );
            })}
          </div>
        </ChartCard>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input placeholder="Search audits..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="input pl-8" />
        </div>
        <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Types</option>
          {AUDIT_TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Status</option>
          <option value="scheduled">Scheduled</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        data={((data?.data && data.data.length > 0) ? data.data : MOCK_AUDITS) as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => setSelectedAudit(row as unknown as Audit)}
        total={data?.meta?.total}
        page={page}
        pageSize={20}
        onPageChange={setPage}
      />

      {/* Detail Drawer */}
      <SideDrawer isOpen={!!selectedAudit} onClose={() => setSelectedAudit(null)}
        title={selectedAudit?.auditCode || ''} subtitle={`${selectedAudit?.lab?.name} · ${selectedAudit?.type}`} width={460}>
        {selectedAudit && (
          <div className="space-y-5">
            <div className="flex items-center gap-2">
              <PillBadge value={selectedAudit.status} showDot />
              <PillBadge value={selectedAudit.type} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-xs text-text-muted">{selectedAudit.auditDate ? 'Audit Date' : 'Scheduled Date'}</p>
                <p className="text-sm font-mono mt-0.5">
                  {format(new Date(selectedAudit.auditDate || selectedAudit.scheduledDate), 'd MMM yyyy')}
                </p>
              </div>
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-xs text-text-muted">Score</p>
                <p className={`text-2xl font-bold font-serif mt-0.5 ${getScoreColor(selectedAudit.score)}`}>{selectedAudit.score}</p>
              </div>
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-xs text-text-muted">Total Findings</p>
                <p className="text-sm font-semibold mt-0.5">{selectedAudit.findings}</p>
              </div>
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-xs text-text-muted">Critical</p>
                <p className={`text-sm font-semibold mt-0.5 ${selectedAudit.criticalFindings > 0 ? 'text-risk-high' : 'text-risk-low'}`}>
                  {selectedAudit.criticalFindings}
                </p>
              </div>
            </div>
            <div>
              <p className="text-xs text-text-muted mb-1">Auditor</p>
              <p className="text-sm font-medium text-text">{selectedAudit.auditorName || '—'}</p>
            </div>
            {selectedAudit.summary && (
              <div>
                <p className="text-xs font-semibold text-text-muted mb-1">Summary</p>
                <p className="text-sm text-text leading-relaxed">{selectedAudit.summary}</p>
              </div>
            )}
            {selectedAudit.nextAuditDate && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-teal/5 border border-teal/20">
                <Calendar size={14} className="text-teal flex-shrink-0" />
                <p className="text-xs text-teal">Next audit: <span className="font-semibold">{format(new Date(selectedAudit.nextAuditDate), 'd MMM yyyy')}</span></p>
              </div>
            )}
            <AuditTrail
              trail={selectedAudit.auditTrail}
              createdBy={selectedAudit.scheduledBy}
              createdAt={selectedAudit.createdAt}
              updatedBy={selectedAudit.conductedBy ?? undefined}
              updatedAt={selectedAudit.updatedAt}
            />

            {/* Export */}
            <div className="flex gap-2 pt-2 border-t border-border">
              <button onClick={() => handleExport(selectedAudit.id, 'pdf')} disabled={!!exporting}
                className="btn-secondary flex-1 text-xs">
                {exporting === 'pdf' ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                Export PDF
              </button>
              <button onClick={() => handleExport(selectedAudit.id, 'xlsx')} disabled={!!exporting}
                className="btn-secondary flex-1 text-xs">
                {exporting === 'xlsx' ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                Export XLSX
              </button>
            </div>
          </div>
        )}
      </SideDrawer>

      {/* Schedule Audit Wizard Modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => { setShowCreate(false); setWizardStep(1); }}
        title="Schedule Audit"
        subtitle={`Step ${wizardStep} of 4 — ${WIZARD_STEPS[wizardStep - 1]}`}
        size="lg"
        footer={
          <div className="flex items-center justify-between w-full">
            <button
              onClick={() => setWizardStep((s) => s - 1)}
              disabled={wizardStep === 1}
              className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Back
            </button>
            {wizardStep < 4 ? (
              <button
                onClick={() => setWizardStep((s) => s + 1)}
                className="btn-primary"
              >
                Next
              </button>
            ) : (
              <button
                onClick={() => scheduleMutation.mutate(wizardData)}
                disabled={scheduleMutation.isPending}
                className="btn-primary"
              >
                {scheduleMutation.isPending ? (
                  <><Loader2 size={13} className="animate-spin" /> Scheduling...</>
                ) : (
                  'Schedule Audit'
                )}
              </button>
            )}
          </div>
        }
      >
        {/* Step progress dots */}
        <div className="flex items-center justify-center gap-3 mb-6">
          {WIZARD_STEPS.map((label, idx) => {
            const step = idx + 1;
            const isActive = step === wizardStep;
            const isDone = step < wizardStep;
            return (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center gap-1">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                      isActive
                        ? 'bg-gold text-white shadow-sm'
                        : isDone
                        ? 'bg-gold/30 text-gold'
                        : 'bg-bg-muted text-text-dim border border-border'
                    }`}
                  >
                    {isDone ? <CheckCircle2 size={14} /> : step}
                  </div>
                  <span className={`text-[10px] font-medium whitespace-nowrap ${isActive ? 'text-gold' : 'text-text-dim'}`}>
                    {label}
                  </span>
                </div>
                {idx < WIZARD_STEPS.length - 1 && (
                  <div className={`h-px flex-1 mt-[-12px] transition-colors ${step < wizardStep ? 'bg-gold/40' : 'bg-border'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {renderWizardStep()}
      </Modal>
    </div>
  );
}
